import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useCategoriesInfinite } from '../hooks/useCategoriesInfinite';
import { useTranslation } from '../contexts/LocaleContext';

interface CategoryMultiSelectProps {
  value: number[];
  onChange: (categoryIds: number[]) => void;
  disabled?: boolean;
  height?: string;
  maxHeight?: string;
  /** 'only' = выбор одной категории (передаёт массив из одного id) */
  mode?: 'only' | 'multiple';
  /** Исключить категорию из списка (например текущую при редактировании) */
  excludeId?: number;
  setChooseCategories?: (categories: {id: number; name: string;}[]) => void
}

export const CategoryMultiSelect: React.FC<CategoryMultiSelectProps> = ({
  value,
  onChange,
  disabled,
  height = '200px',
  maxHeight = '200px',
  mode,
  excludeId,
  setChooseCategories,
}) => {
  const t = useTranslation();
  const { categories, loadMore, hasMore, loading } = useCategoriesInfinite({ pageSize: 7, excludeId });
  const listRef = useRef<HTMLDivElement>(null); 
  const [init,setInit] = useState(false)

  useEffect(() => {
    const loadM = async () =>{
      await loadMore();
      setInit(true)
    }
    loadM()
  }, []);

  useEffect(() => {
    if(init){
      loadMore()
    }
  }, [init]);

  // Initialize setChooseCategories with already selected categories
  useEffect(() => {
    if (setChooseCategories && categories.length > 0 && value.length > 0) {
      const selectedCategories = categories.filter(cat => value.includes(cat.id));
      setChooseCategories(selectedCategories);
    }
  }, [categories, value, setChooseCategories]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      loadMore();
    }
  }, [hasMore, loading, loadMore]);

  const toggle = (id: number) => {
    let newSelection: number[];
    
    if (mode === 'only') {
      newSelection = value.includes(id) ? [] : [id];
      onChange(newSelection);
      return
    }
    if (value.includes(id)) {
      newSelection = value.filter((x) => x !== id);
    } else {
      newSelection = [...value, id];
    }
    
    onChange(newSelection);
    
    // Update setChooseCategories with selected categories
    if (setChooseCategories) {
      const selectedCategories = categories.filter(cat => newSelection.includes(cat.id));
      setChooseCategories(selectedCategories);
    }
  };

  return (
    <div
      ref={listRef}
      onScroll={handleScroll}
      className="border rounded p-2 bg-light category-multiselect-list"
      style={{ height, overflowY: 'auto', maxHeight }}
    >
      {categories.map((cat) => (
        <Form.Check
          key={cat.id}
          type="checkbox"
          id={`cat-${cat.id}`}
          label={cat.name}
          checked={value.includes(cat.id)}
          onChange={() => toggle(cat.id)}
          disabled={disabled}
          className="w-100 category-multiselect-check"
          multiple
        />
      ))}
      {loading && (
        <div className="text-muted small text-center py-1">
          {t('Loading...')}
        </div>
      )}
    </div>
  );
};

export default CategoryMultiSelect;
